import { RectangleElementDto, SceneTypeDto } from "@repo/api/overlays";
import { ElementPositionInput, ElementSizeInput } from "./ElementSliderInput";
import ElementAppearanceSection from "./ElementAppearanceSection";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import ElementBorderSection from "./ElementBorderSection";
import ElementShadowSection from "./ElementShadowSection";
import ElementTransitionSection from "./Transition/ElementTransitionSection";
import { CompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { getResolution } from "@repo/api/utils/composition";
import { ColorPicker } from "@/lib/components/ColorPicker";
import { cn, needsBorder } from "@/lib/utils";
import { getHexWithOpacity } from "@repo/video-engine-i2v/utils/color";

type Props = {
  element: RectangleElementDto;
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings;
};

export default function RectangleElementEditor({
  element,
  sceneType,
  compositionTimings,
}: Props) {
  const composition = useEditorStore((state) => state.video!.compositionJson);
  const updateElement = useEditorStore((state) => state.service.updateElement);

  const resolution = getResolution(composition.aspectRatio);

  const aspectRatio = element.size.w / element.size.h;
  const color = element.backgroundColor.color;
  const opacity = element.backgroundColor.opacity ?? 100;
  const hexWithOpacity = getHexWithOpacity(color, opacity);

  return (
    <div className="px-6">
      <div
        className={cn(
          "h-24 w-24 overflow-hidden rounded-lg border",
          needsBorder(color, opacity) ? "border-input" : "border-transparent",
        )}
        style={{
          backgroundColor: hexWithOpacity,
        }}
      />
      <div className="input-label mt-8">Position & Size</div>
      <div className="flex gap-4">
        <ElementPositionInput
          fieldName="x"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.width}
        />
        <ElementPositionInput
          fieldName="y"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.height}
        />
      </div>
      <div className="mt-2 flex gap-4">
        <ElementSizeInput
          fieldName="w"
          element={element}
          aspectRatio={aspectRatio}
          sceneType={sceneType}
          sliderMax={resolution.width}
        />
        <ElementSizeInput
          fieldName="h"
          element={element}
          aspectRatio={aspectRatio}
          sceneType={sceneType}
          sliderMax={resolution.height}
        />
      </div>
      <ElementAppearanceSection element={element} sceneType={sceneType} />
      <div className="input-label mt-8">Color</div>
      <ColorPicker
        className="w-[296px]"
        value={element.backgroundColor.color}
        opacity={element.backgroundColor.opacity ?? element.opacity ?? 100}
        onChange={(color) => {
          updateElement(element.externalId, (element) => {
            if (!("backgroundColor" in element)) {
              throw new Error("Background color is not in element");
            }
            element.backgroundColor.color = color;
          });
        }}
        onOpacityChange={(opacity) => {
          updateElement(element.externalId, (element) => {
            if (!("backgroundColor" in element)) {
              throw new Error("Background color is not in element");
            }
            element.backgroundColor.opacity = opacity;
          });
        }}
      />
      <ElementBorderSection element={element} sceneType={sceneType} />
      <ElementShadowSection element={element} sceneType={sceneType} />
      <ElementTransitionSection
        element={element}
        fieldName="enterAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
      <ElementTransitionSection
        element={element}
        fieldName="exitAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
    </div>
  );
}
